import {TKey} from '@/translations/i18n'
import {z} from 'zod'

// Define days of week once using Zod enum
export const DayOfWeekEnum = z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'])
export type DayOfWeek = z.infer<typeof DayOfWeekEnum>

// This ensures we get a proper tuple type, not just a generic array
export const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const

// Create a specific record type for UserAvailability to ensure type safety
export type UserAvailability = {
    [K in (typeof DAYS_OF_WEEK)[number]]: {
        enabled: boolean
        timeSlots: TimeSlot[]
    }
}

// A time slot represents a range from start time to end time
export const TimeSlotSchema = z.object({
    start: z.string(), // Format: "HH:MM" (24-hour format)
    end: z.string() // Format: "HH:MM" (24-hour format)
})

export type TimeSlot = z.infer<typeof TimeSlotSchema>

// A day's availability includes whether it's enabled and its time slots
export const DayAvailabilitySchema = z.object({
    enabled: z.boolean(),
    timeSlots: z.array(TimeSlotSchema).default([])
})

export type DayAvailability = z.infer<typeof DayAvailabilitySchema>

// The full availability model covers all days of the week
export const UserAvailabilitySchema = z.object({
    monday: DayAvailabilitySchema,
    tuesday: DayAvailabilitySchema,
    wednesday: DayAvailabilitySchema,
    thursday: DayAvailabilitySchema,
    friday: DayAvailabilitySchema,
    saturday: DayAvailabilitySchema,
    sunday: DayAvailabilitySchema
})

// Translation map for days of the week
export const dayOfWeekTranslationKeys: Record<DayOfWeek, TKey> = {
    monday: 'commons:monday',
    tuesday: 'commons:tuesday',
    wednesday: 'commons:wednesday',
    thursday: 'commons:thursday',
    friday: 'commons:friday',
    saturday: 'commons:saturday',
    sunday: 'commons:sunday'
}

// Default empty availability - using DAYS_OF_WEEK to avoid repetition
export const defaultEmptyAvailability = DAYS_OF_WEEK.reduce(
    (acc, day) => ({
        ...acc,
        [day]: {
            enabled: false,
            timeSlots: []
        }
    }),
    {} as UserAvailability
)
